import { RefObject } from "react";
import { SELECTABLE_SELECTOR } from "./selectable";

type FocusCycleOptions = {
  /** Container whose selectable items participate in the cycle. */
  containerRef: RefObject<HTMLElement>;
  /**
   * Selector for the single element the cycle wraps to/from (e.g. the
   * "Download all" button). Tab from the last item focuses it; Shift+Tab from
   * it focuses the last item.
   */
  boundarySelector: string;
  /** Selector for the cyclable items. Defaults to every selectable item. */
  itemSelector?: string;
};

/**
 * Tab focus cycle for a selectable item grid/list: after the last item, focus
 * wraps to the boundary element, and Shift+Tab from the boundary jumps back to
 * the last item — keeping keyboard traversal contained to the boundary control
 * and the items.
 *
 * Stateless: call from an `onKeyDown` handler. Only acts on the Tab key at the
 * two wrap points; every other keystroke falls through to the browser's normal
 * tab order.
 */
export function handleGridFocusCycle(
  e: React.KeyboardEvent,
  {
    containerRef,
    boundarySelector,
    itemSelector = SELECTABLE_SELECTOR,
  }: FocusCycleOptions
): void {
  if (e.key !== "Tab") return;

  const container = containerRef.current;
  const boundary = document.querySelector<HTMLElement>(boundarySelector);
  if (!container || !boundary) return;

  const items = container.querySelectorAll<HTMLElement>(itemSelector);
  const lastItem = items[items.length - 1];
  if (!lastItem) return;

  const active = document.activeElement;
  if (!e.shiftKey && active === lastItem) {
    e.preventDefault();
    boundary.focus();
    boundary.scrollIntoView({ block: "nearest" });
  } else if (e.shiftKey && active === boundary) {
    e.preventDefault();
    lastItem.focus();
    lastItem.scrollIntoView({ block: "nearest" });
  }
}
